import Spin from 'antd/lib/spin';
import * as React from 'react';
import { MehOutlined } from '@ant-design/icons';
import Result from 'antd/lib/result';
import { antGrey } from '../styles/globalStyles';
import Button from 'antd/lib/button';
import { StepRunsSummaryComponentProps } from '../ValidatedRouteComponents';
import { useSelector } from 'react-redux';
import { selectApp, selectIsStepRunPreviouslyFetched, selectRelease, selectStepRunGraph } from '@/selectors/selectors';
import { RootState } from '@/reducers/reducers';
import { FetchableEntityTypeV2 } from '@/entityType';
import { useGetEntityV2 } from '@/utils/hooks/helpers';
import { Nullable, WorkflowRunComponentProps } from '@/typescriptHelpers';

export const withStepRunsSummary = <P extends object>(
    WrappedComponent: React.ComponentType<P & StepRunsSummaryComponentProps>,
) => {
    const StepRunsSummaryWrapper: React.FC<
        P &
            StepRunsSummaryComponentProps & {
                showLoader?: boolean;
                showError?: boolean;
                withTracking?: boolean;
            }
    > = (props) => {
        const { orgId, appId, releaseId, groupIndex, archetypeGroupIndex, showLoader = true, showError = true } = props;

        const stepRunGraph = useSelector((state: RootState) => selectStepRunGraph(state, releaseId));

        const stepGroup = stepRunGraph.stepGroups?.[groupIndex];
        const archetypeGroup = stepGroup?.stepArchetypeGroups?.[archetypeGroupIndex];

        const workflowRunId = useSelector((state: RootState) => {
            const release = selectRelease(state, releaseId);
            return release.workflowRunId;
        });

        const workflowId = useSelector((state: RootState) => {
            const app = selectApp(state, appId);
            return app.workflowId;
        });

        const workflowRunComponentProps: Nullable<WorkflowRunComponentProps> =
            workflowRunId && workflowId ?
                {
                    orgId,
                    workflowId,
                    workflowRunId,
                }
            :   null;

        const stepRunIds =
            archetypeGroup?.stepRunStatusDatum.map((stepRunStatusDatum) => stepRunStatusDatum.stepRunId) ?? [];

        const [fetchStepRunsSummary, isLoadingStepRunsSummary, stepRunsSummaryFetchError] = useGetEntityV2(
            FetchableEntityTypeV2.StepRunsSummary,
            stepRunIds.join(','),
        );

        const onClickFetchStepRunsSummary = () => {
            fetchStepRunsSummary({
                stepRunIds,
                ...workflowRunComponentProps,
            });
        };

        React.useEffect(() => {
            onClickFetchStepRunsSummary();
        }, [workflowRunId, workflowId, groupIndex, archetypeGroupIndex]);

        const isPreviouslyFetched = useSelector((state: RootState) =>
            stepRunIds.every((stepRunId) => selectIsStepRunPreviouslyFetched(state, stepRunId)),
        );

        return (
            <>
                {isLoadingStepRunsSummary && !isPreviouslyFetched && showLoader && (
                    <div
                        style={{
                            display: 'flex',
                            flexGrow: 100,
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Spin size="small" />
                    </div>
                )}
                {stepRunsSummaryFetchError && showError && (
                    <div
                        style={{
                            display: 'flex',
                            flexGrow: 1,
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Result
                            icon={<MehOutlined style={{ color: antGrey.grey5 }} />}
                            subTitle="Error fetching step runs summary"
                            extra={<Button onClick={onClickFetchStepRunsSummary}>Try again</Button>}
                        />
                    </div>
                )}
                {isPreviouslyFetched && (
                    <WrappedComponent
                        {...props}
                        orgId={orgId}
                        appId={appId}
                        releaseId={releaseId}
                        groupIndex={groupIndex}
                        archetypeGroupIndex={archetypeGroupIndex}
                    />
                )}
            </>
        );
    };

    // Set display name for easier debugging
    const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    StepRunsSummaryWrapper.displayName = `withStepRunsSummary(${wrappedComponentName})`;

    return StepRunsSummaryWrapper;
};
